import { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import Linkify from 'react-linkify';
import { pipe } from 'fp-ts/function';
import { TextKey, DataTestId, FeatureFlag } from '@mews-commander/enums';
import { FeatureFlagService, LocalizedText, lt, PLATFORM_STATUS_URL, withFeatureFlagService } from '@mews/framework';
import { AdaChatBotProvider } from '@mews-commander/vendor/ada-chat-bot';
import { createUUID } from '@mews/std';
import { PlatformEnvironment, UnicodeEntity } from '@mews/enums';
import { Appbar, Container, IconName, Link, Stack, withBreakpointRange } from '@optimus-web/core';
import {
    getBreakpointRangeDefault,
    isMobile,
    isMobileOrTablet,
    NewTabLink,
    TextWithLinks,
    WithBreakpointRange,
} from '@mews-commander/ui';
import { SystemWideBanner } from '@mews/b2b-ui';
import { deferFocus, localizeText } from '@mews-commander/utils';
import { getCurrentEnterpriseUserPreferredServiceId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    isCurrentEnterpriseApplicationProvider,
    isCurrentEnterpriseContractWarningBannerEnabled,
    isCurrentEnterpriseContractErrorBannerEnabled,
    isCurrentEnterpriseOverdue,
    getCurrentEnterpriseSubscription,
} from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    canCurrentEmployeeUseTimeline,
    canCurrentEmployeeManageReservations,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { NotificationMenu } from '../../notifications';
import SearchMenu from '../components/SearchMenu';
import LogoMenu from '../components/LogoMenu';
import UserMenu from '../UserMenu/UserMenu';
import SidebarButton from '../components/SidebarButton';
import { NavbarIconButton } from '../../../components';
import TimelineButton from '../components/TimelineButton';
import ReservationButton from '../components/ReservationButton';
import ReservationOverviewButton from '../components/ReservationOverviewButton';
import { getPlatformEnvironment } from '../../app/reducers/configuration';
import { closeAppSidebar, openAppSidebar } from '../../appSidebar/actions';
import { isAppSidebarOpened } from '../../appSidebar/reducer';
import HelpMenu from '../components/HelpMenu';
import PlatformBadge from '../components/PlatformBadge';
import { AssistantChatButton } from '../components/AssistantChatButton';
import { ExpiredSsoBanner } from '../components/ExpiredSsoBanner';
import { canCurrentEmployeeUseSearchbar } from '../selectors';
import { GoLiveBanner } from '../../onboardingGoLive/GoLiveBanner';
import { AppNavbarElement } from './styles';

interface Props extends WithBreakpointRange {
    canManageReservations?: boolean;
    canUseTimeline?: boolean;
    canUseSearchbar?: boolean;
    isOverdue?: boolean;
    deferFocus: $TSFixMeFunction;
    hasOldContract?: boolean;
    featureFlagService: FeatureFlagService;
}

type State = $TSFixMe;

const LinkComponent = (decoratedHref: string, decoratedText: string, key: number) => (
    <NewTabLink url={decoratedHref} key={key}>
        {decoratedText}
    </NewTabLink>
);

class AppNavBar extends Component<Props, State> {
    mobileSearchField: $TSFixMe;
    assistantEnabled = this.props.featureFlagService.hasFlag(FeatureFlag.assistantEnabled);

    state = {
        mobileSearchVisible: false,
        scope: createUUID(),
    };

    UNSAFE_componentWillReceiveProps(nextProps: $TSFixMe) {
        const nextBreakPointRange = this.assistantEnabled ? getBreakpointRangeDefault() : nextProps.breakpointRange;
        if (this.state.mobileSearchVisible && !isMobileOrTablet(nextBreakPointRange)) {
            this.setState({ mobileSearchVisible: false });
        }
    }

    handleToggleClick = () =>
        this.setState(
            (state: $TSFixMe) => ({
                mobileSearchVisible: !state.mobileSearchVisible,
            }),
            () => {
                if (this.state.mobileSearchVisible && this.mobileSearchField) {
                    this.props.deferFocus(this.mobileSearchField);
                }
            }
        );

    handleToggleSidebar = () => {
        if ((this.props as $TSFixMe).isSidebarOpen) {
            (this.props as $TSFixMe).closeSidebar();
        } else {
            (this.props as $TSFixMe).openSidebar();
        }
    };

    mobileSearchInnerRef = (c: $TSFixMe) => (this.mobileSearchField = c);

    render() {
        const { scope, mobileSearchVisible } = this.state;
        const {
            isOverdue,
            canUseTimeline,
            canUseSearchbar,
            canManageReservations,
            // @ts-expect-error $TSFixMeError: Property 'platformEnvironment' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            platformEnvironment,
            // @ts-expect-error $TSFixMeError: Property 'serviceId' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            serviceId,
            // @ts-expect-error $TSFixMeError: Property 'isApplicationProvider' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            isApplicationProvider,
            // @ts-expect-error $TSFixMeError: Property 'contractErrorBannerEnabled' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            contractErrorBannerEnabled,
            // @ts-expect-error $TSFixMeError: Property 'contractWarningBannerEnabled' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            contractWarningBannerEnabled,
            // @ts-expect-error $TSFixMeError: Property 'contractWarningBannerEnabled' does not exist on type 'Readonly<Props> & Readonly<{ children?: ReactNode; }>'.
            enterpriseSubscription,
            featureFlagService,
        } = this.props;

        const breakpointRange = this.assistantEnabled ? getBreakpointRangeDefault() : this.props.breakpointRange;
        const isPhone = isMobile(breakpointRange);
        const isTimelineButtonVisible = canUseTimeline && !isPhone;
        const isReservationButtonVisible = canManageReservations && !isPhone;
        const isMobileSearchVisible = canUseSearchbar && isMobileOrTablet(breakpointRange);
        const isReservationOverviewButtonVisible = canManageReservations && !isPhone;
        const isNotificationMenuVisible = !isApplicationProvider;

        const searchMenuProps = {
            handleToggleClick: this.handleToggleClick,
            onHideClick: this.handleToggleClick,
            mobileSearchOpened: mobileSearchVisible,
            ref: this.mobileSearchInnerRef,
            scope,
        };

        const isPlatformBadgeVisible = !isPhone && PlatformEnvironment.Production !== platformEnvironment;

        const isAccountBalanceStatementLinkEnabled = featureFlagService.hasFlag(
            FeatureFlag.accountBalanceStatementLink
        );
        const isPciProxyMaintenanceAppBannerEnabled = featureFlagService.hasFlag(
            FeatureFlag.pciProxyMaintenanceAppBannerEnabled
        );

        return (
            <AppNavbarElement data-test-id={DataTestId.AppNavbar}>
                <ExpiredSsoBanner />
                {isOverdue ? (
                    <SystemWideBanner
                        variant="danger"
                        text={
                            isAccountBalanceStatementLinkEnabled ? (
                                <TextWithLinks
                                    links={[
                                        {
                                            id: 'accountStatementLink',
                                            url: `https://mews.com/account-statement/${enterpriseSubscription?.crmIdentifier}`,
                                            text: localizeText(TextKey.Here),
                                        },
                                    ]}
                                    text={localizeText(TextKey.OverdueWarningMessage)}
                                />
                            ) : (
                                <LocalizedText textKey={TextKey.OverdueWarningMessageOld} />
                            )
                        }
                        showIcon
                        centered
                    />
                ) : null}
                {contractErrorBannerEnabled ? (
                    <SystemWideBanner
                        variant="info"
                        text={
                            <Linkify componentDecorator={LinkComponent}>
                                {localizeText(TextKey.ContractErrorBannerEnabled)}
                            </Linkify>
                        }
                        showIcon
                        centered
                    />
                ) : null}
                {contractWarningBannerEnabled ? (
                    <SystemWideBanner
                        variant="warning"
                        text={<LocalizedText textKey={TextKey.ContractWarningBannerEnabled} />}
                        showIcon
                        centered
                    />
                ) : null}
                {isPciProxyMaintenanceAppBannerEnabled ? (
                    <SystemWideBanner
                        variant="info"
                        text={
                            <Stack itemAlignment="center">
                                {lt('PciProxyMaintenanceSystemBannerText')}
                                {UnicodeEntity.NoBreakSpace}
                                <Link href={PLATFORM_STATUS_URL} target="_blank" rel="noreferrer" inherit>
                                    {lt('PciProxyMaintenanceSystemBannerLink')}
                                </Link>
                            </Stack>
                        }
                        showIcon
                        centered
                    />
                ) : null}
                <GoLiveBanner />
                <Appbar
                    leftSide={
                        <Stack itemAlignment="center" fill>
                            <SidebarButton onClick={this.handleToggleSidebar} />
                            <LogoMenu />
                            {isPlatformBadgeVisible && <PlatformBadge platformEnvironment={platformEnvironment} />}
                            {canUseSearchbar && <SearchMenu {...searchMenuProps} />}
                        </Stack>
                    }
                    rightSide={
                        <Container phoneOnly={{ padding: '0' }} tabletUp={{ padding: ['0', '100', '0', '200'] }}>
                            <Stack spacing="200">
                                <Stack spacing="100" itemAlignment="center">
                                    {isMobileSearchVisible && (
                                        <NavbarIconButton
                                            onClick={this.handleToggleClick}
                                            icon={IconName.Search}
                                            title={<LocalizedText textKey={TextKey.Search} />}
                                        />
                                    )}
                                    {/* @ts-expect-error $TSFixMeError: Type '{ serviceId: any; }' is not assignable to type 'IntrinsicAttributes & Omit<Props, never>'. */}
                                    {isTimelineButtonVisible && <TimelineButton serviceId={serviceId} />}
                                    {/* @ts-expect-error $TSFixMeError: Type '{}' is missing the following properties from type 'Omit<Props, never>': title, onClick, url, icon */}
                                    {isReservationButtonVisible && <ReservationButton />}
                                    {isReservationOverviewButtonVisible && (
                                        /* @ts-expect-error $TSFixMeError: Type '{ serviceId: any; }' is not assignable to type 'IntrinsicAttributes & Omit<Props, never>'. */
                                        <ReservationOverviewButton serviceId={serviceId} />
                                    )}
                                    {isNotificationMenuVisible && <NotificationMenu />}
                                    {!isPhone && (
                                        <AdaChatBotProvider>
                                            <HelpMenu />
                                        </AdaChatBotProvider>
                                    )}
                                    <AssistantChatButton />
                                </Stack>
                                <UserMenu />
                            </Stack>
                        </Container>
                    }
                />
            </AppNavbarElement>
        );
    }
}

export default pipe(
    AppNavBar,
    withFeatureFlagService,
    withBreakpointRange,
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    connect(
        createStructuredSelector({
            platformEnvironment: getPlatformEnvironment,
            canManageReservations: canCurrentEmployeeManageReservations,
            canUseTimeline: canCurrentEmployeeUseTimeline,
            // @ts-expect-error $TSFixMeError: No overload matches this call.
            canUseSearchbar: canCurrentEmployeeUseSearchbar,
            isApplicationProvider: isCurrentEnterpriseApplicationProvider,
            isOverdue: isCurrentEnterpriseOverdue,
            isSidebarOpen: isAppSidebarOpened,
            serviceId: getCurrentEnterpriseUserPreferredServiceId,
            contractErrorBannerEnabled: isCurrentEnterpriseContractErrorBannerEnabled,
            contractWarningBannerEnabled: isCurrentEnterpriseContractWarningBannerEnabled,
            enterpriseSubscription: getCurrentEnterpriseSubscription,
        }),
        {
            openSidebar: openAppSidebar,
            closeSidebar: closeAppSidebar,
            deferFocus,
        }
    )
);
